import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Typography,  useTheme, useMediaQuery, Switch } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@apollo/client/react";
import { useNavigate } from "react-router-dom";
import { GET_INSTRUCTOR_LIST } from "../graphql/query";
import themestyle from "../theme";
import type { GetInstructorListResponse, InstructorListItem } from "../interfaces/instructor.interface";
import DataGrids from "./DataGrid";

// Display labels for the instructor's class type (Type column).
const CLASS_TYPE_LABELS: Record<string, string> = {
  STYLE_BASED: "Style Based",
  CHOREOGRAPHY: "Choreography",
  KRUMP: "Krump",
};

type AllInstructorsTableProps = {
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string, name: string) => void;
  onStatusConfirm?: (id: string, name: string, currentStatus: boolean) => void;
  refreshTrigger?: number;
};

const AllInstructorsTable: React.FC<AllInstructorsTableProps> = ({
  onView,
  onEdit,
  onDelete,
  onStatusConfirm,
  refreshTrigger,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchQuery, setSearchQuery] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });

  const { data, loading, refetch, previousData } = useQuery<GetInstructorListResponse>(GET_INSTRUCTOR_LIST, {
    variables: {
      page: paginationModel.page + 1,
      perPage: paginationModel.pageSize,
      search: searchQuery || "",
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (refreshTrigger) refetch();
  }, [refreshTrigger]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, pageSize: 5, page: 0 }));
  }, [isMobile]);

  const rows = useMemo(() => {
    const dataList: InstructorListItem[] = data?.getInstructorList?.data ?? [];
    return dataList.map((row, index) => {
      const serial = paginationModel.page * paginationModel.pageSize + index + 1;
      return {
        ...row,
        serial: serial.toString().padStart(2, "0"),
      };
    });
  }, [data, paginationModel.page, paginationModel.pageSize]);

  const rowCount = data?.getInstructorList?.totalCount ?? previousData?.getInstructorList?.totalCount ?? 0;

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "serial",
        headerName: "S.No",
        minWidth: 50,
        flex: 0.3,
        sortable: false,
      },
      {
        field: "instructorId",
        headerName: "Instructor Id",
        flex: 0.6,
        minWidth: 100,
        valueGetter: (params) => params.row.instructorId || "-",
      },
      {
        field: "fullname",
        headerName: "Full Name",
        flex: 0.9,
        minWidth: 120,
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1.1,
        minWidth: 140,
      },
      {
        field: "classStyle",
        headerName: "Class",
        flex: 0.7,
        minWidth: 100,
        valueGetter: (params) => params.row.classStyle || "-",
      },
      {
        field: "classType",
        headerName: "Type",
        flex: 0.7,
        minWidth: 110,
        valueGetter: (params) =>
          CLASS_TYPE_LABELS[params.row.classType] || params.row.classType || "-",
      },
      {
        field: "contactNumber",
        headerName: "Phone Number",
        flex: 0.7,
        minWidth: 120,
        valueGetter: (params) => params.row.contactNumber || "-",
      },
      {
        field: "isActive",
        headerName: "Status",
        flex: 0.7,
        minWidth: 120,
        sortable: false,
        renderCell: (params) => {
          const isActive = Boolean(params.row.isActive);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Switch
                checked={isActive}
                onChange={() => onStatusConfirm?.(params.row.id, params.row.fullname, isActive)}
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#9BCB4A",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#9BCB4A",
                  },
                  "& .MuiSwitch-track": {
                    backgroundColor: "#D4D8DD",
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{ color: isActive ? "#111827" : "#8A95A6", fontWeight: 500 }}
              >
                {isActive ? "Active" : "Inactive"}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 120,
        flex: 0.6,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <IconButton
              size="small"
              title="Instructor Report"
              onClick={() => navigate(`/instructors/report/${params.row.id}`)}
              sx={{
                border: "1px solid #C7D9F5",
                color: "#1565C0",
                width: 28,
                height: 28,
              }}
            >
              <AssessmentOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              title="View Instructor"
              onClick={() => onView(params.row.id)}
              sx={{
                border: "1px solid #D1E7C7",
                color: themestyle.colors.buttonGreen,
                width: 28,
                height: 28,
              }}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              title="Delete Instructor"
              onClick={() => onDelete(params.row.id, params.row.fullname)}
              sx={{
                border: "1px solid #F2B6B6",
                color: "#E53935",
                width: 28,
                height: 28,
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    [onView, onDelete, onStatusConfirm, navigate],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        backgroundColor: "#F7FAF3",
        borderRadius: 3,
      }}
    >

      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          border: "1px solid #E5E7EB",
          boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
          maxWidth: "100%",
        }}
      >
        <DataGrids
          rows={rows}
          columns={columns}
          toolbar
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          toolbarShowAddIcon={false}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 20]}
          getRowId={(row: any) => row.id}
          onMobileView={(row: any) => onView(row.id)}
          onMobileDelete={(row: any) => onDelete(row.id, row.fullname)}
          onMobileReport={(row: any) => navigate(`/instructors/report/${row.id}`)}
          onMobileStatusToggle={(row: any) => onStatusConfirm?.(row.id, row.fullname, !!row.isActive)}
          loading={loading}
          sx={{
            height: { xs: "auto", sm: "60vh", md: "60vh" },
            minHeight: 320,
            width: "100%",
            maxWidth: "100%",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#F9FAFB",
              borderBottom: "1px solid #E5E7EB",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#F7FBF5",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #EEF2F7",
              fontSize: "13px",
              whiteSpace: "nowrap",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default AllInstructorsTable;
